// src/pages/PricingPage.jsx
import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  CheckCircle,
  TrendingUp,
  BarChart3,
  Calculator,
  FileText,
  Zap,
  Clock,
  ChevronDown,
  Target,
  Lightbulb,
  PieChart,
  Package,
  CreditCard,
  AlertCircle,
  Eye,
  TrendingDown,
  Award,
  Scale,
  Palette
} from 'lucide-react';
import PricingCard from '../components/Pricing/PricingCard';
import Footer from '../components/Footer';

const PricingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  // Color constants for inline styles
  const colors = {
    navy: '#2c365e',
    purple: '#bdb4e2',
    slate: '#484d6d',
    lavender: '#a7adc6',
    lightCream: '#f5f2f0',
    white: '#ffffff'
  };

  // Handle scrolling to hash on page load
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (hash === '#subscription-card' || hash === '#pricing-section') {
        setTimeout(() => {
          const elementId = hash.replace('#', '');
          const element = document.getElementById(elementId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    };

    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    
    return () => {
      window.removeEventListener('hashchange', scrollToHash);
    };
  }, []);

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const features = [
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Full-Cost Profitability Analysis",
      description: "See TRUE profit margins after ALL costs: team time, direct expenses, and allocated overhead. The same comprehensive method Fortune 500 companies use."
    },
    {
      icon: <Calculator className="w-6 h-6" />,
      title: "Cost Discovery Wizard",
      description: "Uncover hidden time and overhead costs in under 15 minutes. No accounting degree needed, just answer simple questions."
    },
    {
      icon: <PieChart className="w-6 h-6" />,
      title: "Per-Offering Analysis",
      description: "Compare profitability across all your products and services. Discover which offerings drive profit and which secretly drain it."
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Strategic Opportunities",
      description: "Get personalized recommendations based on your actual data. Know exactly what to scale, what to fix, and what to stop doing."
    },
    {
      icon: <AlertCircle className="w-6 h-6" />,
      title: "Action Items Generator",
      description: "Get specific next steps to improve each offering"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "What-If Scenarios",
      description: "Test pricing changes and see profit impact instantly"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Overhead Allocation",
      description: "Accurately distribute shared expenses across offerings with multiple allocation methods"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Time Cost Tracking",
      description: "Value your time properly with automated calculations of labor costs"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Professional Reports",
      description: "Export detailed insights for team meetings, investor presentations, or your records"
    }
  ];

  const businessTypes = [
    {
      icon: <Scale className="w-8 h-8" />,
      title: "Professional Service Firms",
      description: "Established firms billing for expertise, advice, and professional knowledge",
      examples: ["Law Firms & Legal Services", "Accounting & Tax Services", "Financial Planning & Wealth Management", "Medical & Therapy Practices", "Business & IT Consulting"]
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: "Creative & Technical Services",
      description: "Service providers who blend creativity, technical skills, and client deliverables",
      examples: ["Marketing & PR Firms", "Web Development & Design", "Photography & Video Production", "Architecture & Interior Design", "Content & Copywriting Services"]
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: "Specialized Creators & Educators",
      description: "Experts who package knowledge and custom products into multiple offerings",
      examples: ["Online Course Creators", "Custom Product Makers", "Training & Development Firms", "Membership Communities", "Educational Services"]
    }
  ];

  const faqs = [
    {
      question: "How quickly will I see results?",
      answer: "Most users discover actionable insights within their first session. The initial analysis is straightforward - just input your offerings and costs, and you'll immediately see which offerings are truly profitable and which are draining your resources."
    },
    {
      question: "Why are your profit margins different from what my accountant shows?",
      answer: "Great question! Your accountant likely shows gross profit (revenue minus direct costs only), which looks impressive but hides reality. We use Full-Cost Profitability Analysis, including ALL costs like your time, team time, and overhead, so you see TRUE profitability per offering. A 25% margin in Profit Clarity means you're crushing it, not struggling. It's the difference between feeling rich and actually being profitable."
    },
    {
      question: "Do I need to be good with numbers?",
      answer: "Not at all! The app is designed for business owners, not accountants. We guide you through each step with clear explanations and helpful tooltips. If you can answer basic questions about your business, you can use Profit Clarity."
    },
    {
      question: "I already use QuickBooks. Why do I need this?",
      answer: "QuickBooks is great for overall bookkeeping! Profit Clarity is different - we analyze profitability PER OFFERING using Full-Cost methodology. Your accountant shows you what happened last year. We show you which specific products and services are making or losing money RIGHT NOW, so you know what to scale."
    },
    {
      question: "What if I have multiple revenue streams?",
      answer: "Perfect! The app is designed to analyze up to 8 different offerings, whether they're services, digital products, or physical products. You can compare them side-by-side to see which ones deserve more of your time and resources."
    },
    {
      question: "How is this different from a spreadsheet?",
      answer: "Spreadsheets take hours to build and require constant manual updates. Profit Clarity gives you insights in minutes with auto-calculated profit margins. More importantly, we don't just show numbers - we provide strategic recommendations about what to do next based on your data."
    },
    {
      question: "Can I export my data?",
      answer: "Yes! You can generate professional PDF reports of your analysis, perfect for team meetings, investor presentations, or your own records. All your data is also saved securely in your account for future reference."
    },
    {
      question: "What if I don't have time to set this up?",
      answer: "You don't have time NOT to know which offerings are draining profit. Most users get their first insights in under 15 minutes. The Cost Discovery Wizard walks you through step-by-step. Once set up, updates take just minutes, and could reveal why you're working 60-hour weeks but not seeing the profit you deserve."
    },
    {
      question: "What happens after the free trial?",
      answer: "After your 14-day free trial, you'll be automatically enrolled in your chosen plan (monthly or annual). You can cancel anytime before the trial ends with no charge. We'll send you reminder emails so you're never caught off guard."
    },
    {
      question: "Does this seem complicated?",
      answer: "Actually, the opposite! Most users get their first insights in under 15 minutes. The Cost Discovery Wizard walks you through step-by-step. No accounting degree needed - just answer simple questions about your offerings, and we handle the complex calculations."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Absolutely. Cancel anytime with no contracts or commitments. We're confident that once you see which offerings are truly profitable, you won't want to go back to guessing."
    },
    {
      question: "Will this work for my type of business?",
      answer: "If you're a service provider, digital product creator, or specialized product business generating $100K-$5M annually with up to 8 distinct offerings, absolutely! Our Full-Cost Profitability Analysis works for professional service firms, creative services, technical consultants, course creators, membership sites, custom product businesses, and more."
    }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.lightCream }}>
      {/* Navigation */}
      <nav 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          backgroundColor: scrolled ? colors.navy : `${colors.navy}e6`,
          backdropFilter: !scrolled ? 'blur(8px)' : 'none',
          padding: scrolled ? '0.75rem 0' : '1.5rem 0',
          transition: 'all 0.3s ease',
          boxShadow: scrolled ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none'
        }}
      >
        <div className="container mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex flex-col">
                <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.white }}>
                  Profit Clarity
                </span>
                <span style={{ fontSize: '0.75rem', color: colors.lavender, marginTop: '0.25rem' }}>
                  by Scaling with Strategy
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 md:gap-4">
              <a 
                href="/login"
                style={{
                  color: colors.white,
                  fontWeight: '500',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  transition: 'all 0.2s',
                  fontSize: window.innerWidth < 640 ? '0.75rem' : '1rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = colors.purple;
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = colors.white;
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <span className="hidden sm:inline">Log In</span>
                <span className="sm:hidden">Login</span>
              </a>
              <a 
                href="#pricing-section"
                style={{
                  backgroundColor: colors.purple,
                  color: colors.navy,
                  padding: window.innerWidth < 640 ? '0.5rem 0.75rem' : '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  fontSize: window.innerWidth < 640 ? '0.75rem' : '1rem',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.white;
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = colors.purple;
                  e.target.style.transform = 'scale(1)';
                }}
              >
                <span className="hidden sm:inline">Start Free Trial</span>
                <span className="sm:hidden">Trial</span>
              </a>
            </div>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-6 lg:px-8 pt-24">
        {/* Hero Section - LIGHT */}
        <section style={{ paddingTop: '3rem', paddingBottom: '2rem', textAlign: 'center', position: 'relative' }}>
          {/* Animated background elements */}
          <div style={{ position: 'absolute', inset: 0, zIndex: -10 }}>
            <div 
              style={{
                position: 'absolute',
                top: '5rem',
                right: '2.5rem',
                width: '24rem',
                height: '24rem',
                borderRadius: '9999px',
                filter: 'blur(64px)',
                backgroundColor: 'rgba(189, 180, 226, 0.2)',
                animation: 'pulse 4s ease-in-out infinite'
              }}
            />
            <div 
              style={{
                position: 'absolute',
                bottom: '5rem',
                left: '2.5rem',
                width: '24rem',
                height: '24rem',
                borderRadius: '9999px',
                filter: 'blur(64px)',
                backgroundColor: 'rgba(44, 54, 94, 0.2)',
                animation: 'pulse 4s ease-in-out infinite',
                animationDelay: '1s'
              }}
            />
          </div>
          
          <div style={{ position: 'relative', zIndex: 10 }}>
            {/* Launch Badge */}
            <div 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: '#d1fae5',
                color: '#047857',
                padding: '0.5rem 1rem',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '500',
                marginBottom: '1.5rem',
                animation: 'fadeIn 0.6s ease-out'
              }}
            >
              <Zap className="w-4 h-4 mr-2" />
              Limited Time Launch Pricing Available
            </div>

            <h1 
              style={{
                fontSize: window.innerWidth >= 1024 ? '4.5rem' : window.innerWidth >= 768 ? '3.75rem' : '3rem',
                fontWeight: 'bold',
                color: colors.navy,
                marginBottom: '1.5rem',
                lineHeight: 1.2,
                animation: 'slideUp 0.8s ease-out'
              }}
            >
              Your Revenue is<br />
              <span 
                style={{ 
                  backgroundImage: `linear-gradient(to right, ${colors.navy}, ${colors.purple})`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent'
                }}
              >
                Lying to You.
              </span>
            </h1>
            
            <p 
              style={{
                fontSize: window.innerWidth >= 768 ? '1.5rem' : '1.25rem',
                color: colors.slate,
                marginBottom: '1rem',
                maxWidth: '48rem',
                marginLeft: 'auto',
                marginRight: 'auto',
                lineHeight: 1.6,
                animation: 'slideUp 0.8s ease-out 0.1s both'
              }}
            >
              Discover exactly which offerings drive profit and which secretly drain it, then focus on what works.
            </p>
            <p 
              style={{
                fontSize: window.innerWidth >= 768 ? '1.25rem' : '1.125rem',
                color: colors.slate,
                marginBottom: '2rem',
                maxWidth: '42rem',
                marginLeft: 'auto',
                marginRight: 'auto',
                animation: 'slideUp 0.8s ease-out 0.15s both'
              }}
            >
              The same Full-Cost Profitability Analysis Fortune 500 companies use. Now for your business.
            </p>
            
            <div 
              style={{
                display: 'flex',
                flexDirection: window.innerWidth < 640 ? 'column' : 'row',
                gap: '1rem',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '3rem',
                animation: 'slideUp 0.8s ease-out 0.2s both'
              }}
            >
              <a 
                href="#pricing-section"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: colors.purple,
                  color: colors.navy,
                  padding: '1.25rem 2.5rem',
                  borderRadius: '0.5rem',
                  fontWeight: 'bold',
                  fontSize: '1.25rem',
                  transition: 'all 0.2s',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.white;
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 35px 60px -15px rgba(0, 0, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = colors.purple;
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
                }}
              >
                Start Your Free 14-Day Trial
                <ArrowRight className="ml-3" size={24} />
              </a>
            </div>
          </div>
        </section>

        {/* The Problem Section - REFRAMED TO BE MORE POSITIVE */}
        <section style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                style={{
                  fontSize: window.innerWidth >= 768 ? '3rem' : '2.25rem',
                  fontWeight: 'bold',
                  color: colors.navy,
                  marginBottom: '1rem',
                  lineHeight: 1.1
                }}
              >
                The Revenue-Profit Gap<br />Most Business Owners Don't See
              </h2>
              <p 
                style={{
                  fontSize: '1.25rem',
                  color: colors.slate,
                  maxWidth: '48rem',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}
              >
                Traditional accounting shows impressive gross margins, but Full-Cost Profitability Analysis reveals the complete picture—and that's where real growth begins.
              </p>
            </div>

            {/* Real Example Comparison */}
            <div 
              style={{
                backgroundColor: colors.white,
                borderRadius: '1rem',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                padding: '2rem',
                marginBottom: '2rem'
              }}
            >
              <h3 
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: colors.navy,
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}
              >
                Real Example: The $4,500 Website Design Project
              </h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* Traditional View */}
                <div 
                  style={{
                    backgroundColor: '#fffbeb',
                    borderRadius: '0.75rem',
                    padding: '1.5rem',
                    border: '2px solid #fde68a'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div style={{ backgroundColor: '#fef3c7', padding: '0.5rem', borderRadius: '0.5rem' }}>
                      <BarChart3 className="w-6 h-6" style={{ color: '#d97706' }} />
                    </div>
                    <div>
                      <h4 style={{ fontWeight: 'bold', color: '#78350f' }}>Traditional Gross Profit View</h4>
                      <p style={{ fontSize: '0.875rem', color: '#92400e' }}>What Most Systems Show</p>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.navy, marginBottom: '0.75rem' }}>
                      <span>Revenue:</span>
                      <span style={{ fontWeight: '600' }}>$4,500</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.navy, marginBottom: '0.75rem' }}>
                      <span>Direct costs (hosting, stock photos):</span>
                      <span style={{ fontWeight: '600' }}>-$300</span>
                    </div>
                    <div 
                      style={{
                        borderTop: '2px solid #fde68a',
                        paddingTop: '0.75rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <span style={{ fontWeight: 'bold', color: '#78350f' }}>Gross Profit:</span>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 'bold', color: '#78350f' }}>$4,200</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#d97706' }}>93% margin</div>
                      </div>
                    </div>
                  </div>
                  
                  <div 
                    style={{
                      backgroundColor: '#fef3c7',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                      textAlign: 'center'
                    }}
                  >
                    <p style={{ color: '#78350f', fontWeight: '500' }}>The picture most business owners see</p>
                  </div>
                </div>

                {/* Full-Cost View */}
                <div 
                  style={{
                    backgroundImage: 'linear-gradient(to bottom right, #f0fdf4, #dcfce7)',
                    borderRadius: '0.75rem',
                    padding: '1.5rem',
                    border: '2px solid #86efac'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div style={{ backgroundColor: '#dcfce7', padding: '0.5rem', borderRadius: '0.5rem' }}>
                      <Eye className="w-6 h-6" style={{ color: '#16a34a' }} />
                    </div>
                    <div>
                      <h4 style={{ fontWeight: 'bold', color: '#14532d' }}>Full-Cost Profitability View</h4>
                      <p style={{ fontSize: '0.875rem', color: '#15803d' }}>What Profit Clarity Reveals</p>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.navy, marginBottom: '0.75rem' }}>
                      <span>Revenue:</span>
                      <span style={{ fontWeight: '600' }}>$4,500</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.navy, marginBottom: '0.75rem' }}>
                      <span>Direct costs:</span>
                      <span style={{ fontWeight: '600' }}>-$300</span>
                    </div>
                    
                    {/* Time Costs Section - Emphasizing Owner's Time */}
                    <div style={{ marginBottom: '0.5rem', marginTop: '0.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.navy, marginBottom: '0.5rem' }}>
                        <span style={{ fontWeight: '600' }}>Time costs:</span>
                        <span style={{ fontWeight: '600' }}>-$3,300</span>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: colors.slate, marginLeft: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <span>• Owner's time (20 hrs × $125):</span>
                          <span>$2,500</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>• Team member time (8 hrs × $100):</span>
                          <span>$800</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Overhead Section */}
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.navy, marginBottom: '0.25rem' }}>
                        <span style={{ fontWeight: '600' }}>Allocated overhead:</span>
                        <span style={{ fontWeight: '600' }}>-$350</span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: colors.slate, marginLeft: '1rem', fontStyle: 'italic' }}>
                        Software, office rent, insurance, utilities, admin support
                      </div>
                    </div>
                    
                    <div 
                      style={{
                        borderTop: '2px solid #86efac',
                        paddingTop: '0.75rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <span style={{ fontWeight: 'bold', color: '#14532d' }}>True Profit:</span>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 'bold', color: '#14532d' }}>$550</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#16a34a' }}>12% margin</div>
                      </div>
                    </div>
                  </div>
                  
                  <div 
                    style={{
                      backgroundColor: '#dcfce7',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                      textAlign: 'center'
                    }}
                  >
                    <p style={{ color: '#14532d', fontWeight: '500' }}>The clarity that transforms decisions</p>
                  </div>
                </div>
              </div>

              {/* Bottom explanation section */}
              <div 
                style={{
                  marginTop: '2rem',
                  backgroundColor: colors.navy,
                  borderRadius: '0.75rem',
                  padding: '1.5rem',
                  color: colors.white
                }}
              >
                <p style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', textAlign: 'center' }}>
                  Here's the opportunity:
                </p>
                <p style={{ fontSize: '1.125rem', marginBottom: '1rem', textAlign: 'center' }}>
                  When you know your true <span style={{ color: colors.purple }}>12% margin</span>, you can optimize pricing, streamline delivery, and focus on your most profitable work.
                </p>
                
                {/* What gets missed section */}
                <div 
                  style={{
                    marginTop: '1.5rem',
                    paddingTop: '1.5rem',
                    borderTop: `1px solid ${colors.lavender}`
                  }}
                >
                  <p style={{ fontSize: '0.875rem', color: colors.lavender, marginBottom: '0.75rem', fontWeight: '600' }}>
                    What traditional accounting typically misses:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4" style={{ fontSize: '0.875rem' }}>
                    <div>
                      <p style={{ color: colors.purple, fontWeight: '600', marginBottom: '0.25rem' }}>Owner & Team Time:</p>
                      <p style={{ color: colors.white, fontSize: '0.8125rem' }}>
                        Your delivery hours, team labor, revisions, meetings, project management, support—most owners forget to value their own time
                      </p>
                    </div>
                    <div>
                      <p style={{ color: colors.purple, fontWeight: '600', marginBottom: '0.25rem' }}>Shared Overhead Costs:</p>
                      <p style={{ color: colors.white, fontSize: '0.8125rem' }}>
                        Software subscriptions, office space, insurance, admin staff, utilities, professional services
                      </p>
                    </div>
                  </div>
                </div>
                
                <p style={{ fontSize: '0.875rem', color: colors.lavender, marginTop: '1.5rem', textAlign: 'center', fontStyle: 'italic' }}>
                  Full-Cost Profitability Analysis transforms guesswork into strategic clarity.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Updated Profit Margin Benchmarks - DARK */}
        <section 
          style={{
            paddingTop: '4rem',
            paddingBottom: '4rem',
            backgroundImage: `linear-gradient(to bottom right, #1f2937, ${colors.navy})`,
            borderRadius: '1.5rem'
          }}
        >
          <div className="max-w-4xl mx-auto px-6">
            <h2 
              style={{
                fontSize: '2.25rem',
                fontWeight: 'bold',
                color: colors.white,
                marginBottom: '1rem',
                textAlign: 'center'
              }}
            >
              Understanding Real Profit Margins
            </h2>
            <p 
              style={{
                fontSize: '1.25rem',
                color: colors.lavender,
                marginBottom: '3rem',
                textAlign: 'center',
                maxWidth: '48rem',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
            >
              Because Full-Cost Profitability includes ALL expenses, our benchmarks reflect true operational excellence
            </p>

            <div className="grid md:grid-cols-4 gap-6">
              <div 
                style={{
                  backgroundImage: 'linear-gradient(to bottom right, #f0fdf4, #dcfce7)',
                  borderRadius: '0.75rem',
                  padding: '1.5rem',
                  border: '2px solid #86efac'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.75rem' }}>
                  <Award className="w-10 h-10" style={{ color: '#16a34a' }} />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#15803d', marginBottom: '0.5rem' }}>30%+</div>
                  <div style={{ fontWeight: 'bold', color: '#14532d', marginBottom: '0.5rem' }}>Excellent</div>
                  <p style={{ fontSize: '0.875rem', color: '#166534' }}>Outstanding performance with all costs included</p>
                </div>
              </div>

              <div 
                style={{
                  backgroundImage: 'linear-gradient(to bottom right, #f0fdfa, #ccfbf1)',
                  borderRadius: '0.75rem',
                  padding: '1.5rem',
                  border: '2px solid #5eead4'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.75rem' }}>
                  <CheckCircle className="w-10 h-10" style={{ color: '#0d9488' }} />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#0f766e', marginBottom: '0.5rem' }}>20-29%</div>
                  <div style={{ fontWeight: 'bold', color: '#134e4a', marginBottom: '0.5rem' }}>Healthy</div>
                  <p style={{ fontSize: '0.875rem', color: '#115e59' }}>Sustainable profitability</p>
                </div>
              </div>

              <div 
                style={{
                  backgroundImage: 'linear-gradient(to bottom right, #fffbeb, #fef3c7)',
                  borderRadius: '0.75rem',
                  padding: '1.5rem',
                  border: '2px solid #fcd34d'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.75rem' }}>
                  <AlertCircle className="w-10 h-10" style={{ color: '#d97706' }} />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#b45309', marginBottom: '0.5rem' }}>10-19%</div>
                  <div style={{ fontWeight: 'bold', color: '#78350f', marginBottom: '0.5rem' }}>Fair</div>
                  <p style={{ fontSize: '0.875rem', color: '#92400e' }}>Viable but room for improvement</p>
                </div>
              </div>

              <div 
                style={{
                  backgroundImage: 'linear-gradient(to bottom right, #fef2f2, #fee2e2)',
                  borderRadius: '0.75rem',
                  padding: '1.5rem',
                  border: '2px solid #fca5a5'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.75rem' }}>
                  <TrendingDown className="w-10 h-10" style={{ color: '#dc2626' }} />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#b91c1c', marginBottom: '0.5rem' }}>&lt;10%</div>
                  <div style={{ fontWeight: 'bold', color: '#7f1d1d', marginBottom: '0.5rem' }}>Needs Attention</div>
                  <p style={{ fontSize: '0.875rem', color: '#991b1b' }}>Immediate action required</p>
                </div>
              </div>
            </div>

            <div 
              style={{
                marginTop: '2rem',
                backgroundColor: colors.white,
                borderRadius: '0.75rem',
                padding: '1.5rem'
              }}
            >
              <p style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', textAlign: 'center' }}>
                <span style={{ color: colors.purple }}>Key Insight:</span>
              </p>
              <p style={{ textAlign: 'center', color: colors.navy }}>
                A 25% margin in Profit Clarity means you're crushing it, not struggling. Traditional gross margins of 50-70% often hide 10-15% actual profitability.
              </p>
            </div>
          </div>
        </section>

        {/* Three Steps to Profit Clarity - LIGHT */}
        <section 
          style={{
            paddingTop: '4rem',
            paddingBottom: '4rem',
            backgroundImage: `linear-gradient(to bottom right, ${colors.lightCream}, ${colors.white})`,
            borderRadius: '1.5rem'
          }}
        >
          <div className="text-center mb-12">
            <h2 
              style={{
                fontSize: window.innerWidth >= 768 ? '3rem' : '2.25rem',
                fontWeight: 'bold',
                color: colors.navy,
                marginBottom: '1rem'
              }}
            >
              Three Steps to Profit Clarity
            </h2>
            <p 
              style={{
                fontSize: '1.25rem',
                color: colors.navy,
                fontWeight: '500',
                maxWidth: '42rem',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
            >
              Our guided process makes it simple to uncover your true profitability
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                step: "1",
                title: "Input Your Offerings",
                description: "Enter your services, products, or packages with their prices and sales volumes",
                icon: <BarChart3 className="w-8 h-8" />
              },
              {
                step: "2",
                title: "Map Your Costs",
                description: "Our wizard guides you through time costs, direct costs, and overhead allocation",
                icon: <Calculator className="w-8 h-8" />
              },
              {
                step: "3",
                title: "Get Actionable Insights",
                description: "See your true margins and receive recommendations to boost profits",
                icon: <Lightbulb className="w-8 h-8" />
              }
            ].map((item, index) => (
              <div key={index} style={{ position: 'relative' }}>
                <div 
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: '1rem',
                    padding: '2rem',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s',
                    border: '1px solid #f3f4f6',
                    minHeight: '280px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
                    e.currentTarget.style.transform = 'translateY(-0.5rem)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Step number */}
                  <div 
                    style={{
                      position: 'absolute',
                      top: '-1rem',
                      left: '-1rem',
                      width: '3rem',
                      height: '3rem',
                      borderRadius: '9999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.25rem',
                      color: colors.white,
                      backgroundColor: colors.navy,
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    {item.step}
                  </div>

                  {/* Icon */}
                  <div 
                    style={{
                      width: '4rem',
                      height: '4rem',
                      borderRadius: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1rem',
                      marginLeft: 'auto',
                      marginRight: 'auto',
                      marginTop: '1rem',
                      backgroundColor: 'rgba(189, 180, 226, 0.2)'
                    }}
                  >
                    <div style={{ color: colors.navy }}>
                      {item.icon}
                    </div>
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <h3 
                      style={{
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        color: colors.navy,
                        marginBottom: '0.75rem',
                        textAlign: 'center'
                      }}
                    >
                      {item.title}
                    </h3>
                    <p style={{ color: colors.slate, textAlign: 'center', lineHeight: 1.6 }}>
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Everything You Need to Scale Smart - LIGHT */}
        <section style={{ paddingTop: '5rem', paddingBottom: '5rem' }}>
          <div className="text-center mb-16">
            <h2 
              style={{
                fontSize: window.innerWidth >= 768 ? '3rem' : '2.25rem',
                fontWeight: 'bold',
                color: colors.navy,
                marginBottom: '1rem'
              }}
            >
              Everything You Need to Scale Smart
            </h2>
            <p 
              style={{
                fontSize: '1.25rem',
                color: colors.slate,
                maxWidth: '42rem',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
            >
              Comprehensive profitability analysis tools designed for business owners
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <div 
                key={index}
                style={{
                  backgroundColor: colors.white,
                  borderRadius: '0.75rem',
                  padding: '2rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-0.25rem)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div 
                  style={{
                    width: '3.5rem',
                    height: '3.5rem',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem',
                    backgroundColor: 'rgba(189, 180, 226, 0.2)'
                  }}
                >
                  <div style={{ color: colors.navy }}>
                    {feature.icon}
                  </div>
                </div>
                <h3 
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    color: colors.navy,
                    marginBottom: '0.75rem'
                  }}
                >
                  {feature.title}
                </h3>
                <p style={{ color: colors.slate, lineHeight: 1.6 }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Animated Dashboard Preview Section - LIGHT */}
        <section style={{ paddingTop: '5rem', paddingBottom: '5rem' }}>
          <div className="text-center mb-12">
            <h2 
              style={{
                fontSize: window.innerWidth >= 768 ? '3rem' : '2.25rem',
                fontWeight: 'bold',
                color: colors.navy,
                marginBottom: '1rem'
              }}
            >
              See Your Complete Profit Story
            </h2>
            <p 
              style={{
                fontSize: '1.25rem',
                color: colors.slate,
                maxWidth: '48rem',
                marginLeft: 'auto',
                marginRight: 'auto',
                marginBottom: '0.5rem'
              }}
            >
              Intuitive dashboards and visual insights make complex profitability analysis simple
            </p>
            <p 
              style={{
                fontSize: '0.875rem',
                color: colors.purple,
                fontStyle: 'italic'
              }}
            >
              Hover to pause • Auto-scrolling preview
            </p>
          </div>

          <div className="scrolling-dashboard-container">
            <div className="browser-frame">
              {/* Browser Header */}
              <div className="browser-header">
                <div className="browser-dots">
                  <div className="browser-dot dot-red"></div>
                  <div className="browser-dot dot-yellow"></div>
                  <div className="browser-dot dot-green"></div>
                </div>
                <div className="browser-address-bar">
                  <span role="img" aria-label="secure" style={{ marginRight: '8px', color: '#ccc' }}>🔒</span>
                  profitclarity.app/dashboard
                </div>
              </div>

              {/* Dashboard Viewport */}
              <div className="dashboard-viewport">
                <div className="fade-overlay-top"></div>
                <img 
                  src="/images/profit-clarity-dashboard.png" 
                  alt="Complete Profit Clarity Dashboard showing profitability analysis, charts, and strategic insights"
                  className="dashboard-image"
                />
                <div className="fade-overlay-bottom"></div>
              </div>
            </div>
          </div>

          {/* Optional: Key Features Below */}
          <div 
            className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto"
            style={{ marginTop: '3rem' }}
          >
            <div style={{ textAlign: 'center' }}>
              <div 
                style={{
                  width: '3rem',
                  height: '3rem',
                  borderRadius: '0.5rem',
                  backgroundColor: 'rgba(189, 180, 226, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: colors.navy
                }}
              >
                <Eye className="w-6 h-6" />
              </div>
              <h4 style={{ fontWeight: '600', color: colors.navy, marginBottom: '0.5rem' }}>
                Visual Clarity
              </h4>
              <p style={{ fontSize: '0.875rem', color: colors.slate }}>
                Charts and graphs that make your profit story crystal clear
              </p>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div 
                style={{
                  width: '3rem',
                  height: '3rem',
                  borderRadius: '0.5rem',
                  backgroundColor: 'rgba(189, 180, 226, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: colors.navy
                }}
              >
                <Target className="w-6 h-6" />
              </div>
              <h4 style={{ fontWeight: '600', color: colors.navy, marginBottom: '0.5rem' }}>
                Action Items
              </h4>
              <p style={{ fontSize: '0.875rem', color: colors.slate }}>
                Specific next steps generated from your actual data
              </p>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div 
                style={{
                  width: '3rem',
                  height: '3rem',
                  borderRadius: '0.5rem',
                  backgroundColor: 'rgba(189, 180, 226, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: colors.navy
                }}
              >
                <FileText className="w-6 h-6" />
              </div>
              <h4 style={{ fontWeight: '600', color: colors.navy, marginBottom: '0.5rem' }}>
                Professional Reports
              </h4>
              <p style={{ fontSize: '0.875rem', color: colors.slate }}>
                Export detailed insights for meetings and planning
              </p>
            </div>
          </div>
        </section>

        {/* How It's Different Section - DARK */}
        <section 
          style={{
            paddingTop: '4rem',
            paddingBottom: '4rem',
            backgroundImage: `linear-gradient(to bottom right, #1f2937, ${colors.navy})`,
            borderRadius: '1.5rem'
          }}
        >
          <div className="max-w-6xl mx-auto px-6">
            <h2 
              style={{
                fontSize: '2.25rem',
                fontWeight: 'bold',
                color: colors.white,
                marginBottom: '3rem',
                textAlign: 'center'
              }}
            >
              How Profit Clarity is Different
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* vs QuickBooks */}
              <div 
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '0.75rem',
                  padding: '2rem',
                  border: '1px solid rgba(189, 180, 226, 0.3)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <div 
                    style={{
                      backgroundColor: 'rgba(189, 180, 226, 0.3)',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      flexShrink: 0
                    }}
                  >
                    <BarChart3 className="w-8 h-8" style={{ color: colors.white }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 
                      style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: colors.white,
                        marginBottom: '0.75rem'
                      }}
                    >
                      vs. QuickBooks/Accounting Software
                    </h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <AlertCircle className="w-4 h-4" style={{ color: '#fca5a5', flexShrink: 0 }} />
                          <p style={{ fontWeight: '600', color: '#fca5a5' }}>They show history</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1.5rem' }}>
                          <ArrowRight className="w-4 h-4" style={{ color: '#86efac', flexShrink: 0 }} />
                          <p style={{ fontSize: '0.875rem', color: '#86efac', fontWeight: '500' }}>We transform future profit</p>
                        </div>
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <AlertCircle className="w-4 h-4" style={{ color: '#fca5a5', flexShrink: 0 }} />
                          <p style={{ fontWeight: '600', color: '#fca5a5' }}>They report gross margins</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1.5rem' }}>
                          <ArrowRight className="w-4 h-4" style={{ color: '#86efac', flexShrink: 0 }} />
                          <p style={{ fontSize: '0.875rem', color: '#86efac', fontWeight: '500' }}>We reveal true profitability per offering</p>
                        </div>
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <AlertCircle className="w-4 h-4" style={{ color: '#fca5a5', flexShrink: 0 }} />
                          <p style={{ fontWeight: '600', color: '#fca5a5' }}>They track what happened</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1.5rem' }}>
                          <ArrowRight className="w-4 h-4" style={{ color: '#86efac', flexShrink: 0 }} />
                          <p style={{ fontSize: '0.875rem', color: '#86efac', fontWeight: '500' }}>We show you what to do next</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* vs Spreadsheets */}
              <div 
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '0.75rem',
                  padding: '2rem',
                  border: '1px solid rgba(189, 180, 226, 0.3)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <div 
                    style={{
                      backgroundColor: 'rgba(189, 180, 226, 0.3)',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      flexShrink: 0
                    }}
                  >
                    <FileText className="w-8 h-8" style={{ color: colors.white }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 
                      style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: colors.white,
                        marginBottom: '0.75rem'
                      }}
                    >
                      vs. Spreadsheets
                    </h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <AlertCircle className="w-4 h-4" style={{ color: '#fca5a5', flexShrink: 0 }} />
                          <p style={{ fontWeight: '600', color: '#fca5a5' }}>Manual calculations</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1.5rem' }}>
                          <ArrowRight className="w-4 h-4" style={{ color: '#86efac', flexShrink: 0 }} />
                          <p style={{ fontSize: '0.875rem', color: '#86efac', fontWeight: '500' }}>Automatic profit analysis</p>
                        </div>
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <AlertCircle className="w-4 h-4" style={{ color: '#fca5a5', flexShrink: 0 }} />
                          <p style={{ fontWeight: '600', color: '#fca5a5' }}>Hours to build and update</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1.5rem' }}>
                          <ArrowRight className="w-4 h-4" style={{ color: '#86efac', flexShrink: 0 }} />
                          <p style={{ fontSize: '0.875rem', color: '#86efac', fontWeight: '500' }}>Minutes to insights</p>
                        </div>
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <AlertCircle className="w-4 h-4" style={{ color: '#fca5a5', flexShrink: 0 }} />
                          <p style={{ fontWeight: '600', color: '#fca5a5' }}>Just show numbers</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1.5rem' }}>
                          <ArrowRight className="w-4 h-4" style={{ color: '#86efac', flexShrink: 0 }} />
                          <p style={{ fontSize: '0.875rem', color: '#86efac', fontWeight: '500' }}>Provide strategic recommendations</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* vs Payment Processors */}
              <div 
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '0.75rem',
                  padding: '2rem',
                  border: '1px solid rgba(189, 180, 226, 0.3)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <div 
                    style={{
                      backgroundColor: 'rgba(189, 180, 226, 0.3)',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      flexShrink: 0
                    }}
                  >
                    <CreditCard className="w-8 h-8" style={{ color: colors.white }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 
                      style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: colors.white,
                        marginBottom: '0.75rem'
                      }}
                    >
                      vs. Stripe/Payment Processors
                    </h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <AlertCircle className="w-4 h-4" style={{ color: '#fca5a5', flexShrink: 0 }} />
                          <p style={{ fontWeight: '600', color: '#fca5a5' }}>Show revenue collected</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1.5rem' }}>
                          <ArrowRight className="w-4 h-4" style={{ color: '#86efac', flexShrink: 0 }} />
                          <p style={{ fontSize: '0.875rem', color: '#86efac', fontWeight: '500' }}>Show profit after all costs</p>
                        </div>
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <AlertCircle className="w-4 h-4" style={{ color: '#fca5a5', flexShrink: 0 }} />
                          <p style={{ fontWeight: '600', color: '#fca5a5' }}>Track transactions</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1.5rem' }}>
                          <ArrowRight className="w-4 h-4" style={{ color: '#86efac', flexShrink: 0 }} />
                          <p style={{ fontSize: '0.875rem', color: '#86efac', fontWeight: '500' }}>Analyze true profitability</p>
                        </div>
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <AlertCircle className="w-4 h-4" style={{ color: '#fca5a5', flexShrink: 0 }} />
                          <p style={{ fontWeight: '600', color: '#fca5a5' }}>Help you get paid</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1.5rem' }}>
                          <ArrowRight className="w-4 h-4" style={{ color: '#86efac', flexShrink: 0 }} />
                          <p style={{ fontSize: '0.875rem', color: '#86efac', fontWeight: '500' }}>Help you keep more</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Who This Is For - LIGHT */}
        <section style={{ paddingTop: '5rem', paddingBottom: '5rem' }}>
          <div className="text-center mb-16">
            <h2 
              style={{
                fontSize: window.innerWidth >= 768 ? '3rem' : '2.25rem',
                fontWeight: 'bold',
                color: colors.navy,
                marginBottom: '1rem'
              }}
            >
              Built for Real Service & Product Businesses
            </h2>
            <p 
              style={{
                fontSize: '1.25rem',
                color: colors.slate,
                maxWidth: '48rem',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
            >
              Whether you bill for expertise, create custom deliverables, or package specialized knowledge, Profit Clarity helps you see what's truly profitable
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {businessTypes.map((type, index) => (
              <div 
                key={index} 
                style={{
                  backgroundColor: colors.white,
                  borderRadius: '0.75rem',
                  padding: '2rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div 
                  style={{
                    width: '4rem',
                    height: '4rem',
                    borderRadius: '9999px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    backgroundColor: 'rgba(189, 180, 226, 0.2)'
                  }}
                >
                  <div style={{ color: colors.navy }}>
                    {type.icon}
                  </div>
                </div>
                <h3 
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: colors.navy,
                    marginBottom: '1rem',
                    textAlign: 'center'
                  }}
                >
                  {type.title}
                </h3>
                <p 
                  style={{
                    color: colors.slate,
                    marginBottom: '1.5rem',
                    textAlign: 'center'
                  }}
                >
                  {type.description}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {type.examples.map((example, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <CheckCircle className="w-4 h-4" style={{ color: '#16a34a', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.875rem', color: colors.slate }}>{example}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing Section - LIGHT */}
        <section id="pricing-section" style={{ paddingTop: '5rem', paddingBottom: '5rem', scrollMarginTop: '5rem' }}>
          <div className="text-center mb-16">
            <h2 
              style={{
                fontSize: window.innerWidth >= 768 ? '3rem' : '2.25rem',
                fontWeight: 'bold',
                color: colors.navy,
                marginBottom: '1rem'
              }}
            >
              Simple, Transparent Pricing
            </h2>
            <p 
              style={{
                fontSize: '1.25rem',
                color: colors.slate,
                maxWidth: '42rem',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
            >
              Start your 14-day free trial. Lock in launch pricing forever.
            </p>
          </div>

          <div id="subscription-card" className="max-w-2xl mx-auto" style={{ scrollMarginTop: '5rem' }}>
            <PricingCard />
          </div>
        </section>

        {/* FAQs - WHITE */}
        <section 
          style={{
            paddingTop: '5rem',
            paddingBottom: '5rem',
            backgroundColor: colors.white,
            borderRadius: '1.5rem'
          }}
        >
          <div className="max-w-4xl mx-auto">
            <h2 
              style={{
                fontSize: '2.25rem',
                fontWeight: 'bold',
                color: colors.navy,
                marginBottom: '3rem',
                textAlign: 'center'
              }}
            >
              Frequently Asked Questions
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  style={{
                    border: `2px solid ${colors.lavender}`,
                    borderRadius: '0.75rem',
                    overflow: 'hidden',
                    transition: 'all 0.3s'
                  }}
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    style={{
                      width: '100%',
                      padding: '1.5rem 2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: colors.white,
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.lightCream;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = colors.white;
                    }}
                  >
                    <span 
                      style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        color: colors.navy,
                        textAlign: 'left',
                        paddingRight: '1rem'
                      }}
                    >
                      {faq.question}
                    </span>
                    <ChevronDown 
                      className="w-6 h-6"
                      style={{
                        color: colors.purple,
                        transition: 'transform 0.3s',
                        transform: openFaq === index ? 'rotate(180deg)' : 'rotate(0deg)',
                        flexShrink: 0
                      }}
                    />
                  </button>
                  
                  <div 
                    style={{
                      overflow: 'hidden',
                      transition: 'max-height 0.3s ease',
                      maxHeight: openFaq === index ? '24rem' : '0'
                    }}
                  >
                    <div 
                      style={{
                        padding: '1.5rem 2rem',
                        backgroundColor: colors.lightCream,
                        borderTop: `2px solid ${colors.lavender}`
                      }}
                    >
                      <p style={{ color: colors.slate, lineHeight: 1.6 }}>
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA - DARK */}
        <section style={{ paddingTop: '5rem', paddingBottom: '5rem' }}>
          <div 
            style={{
              borderRadius: '1.5rem',
              padding: '3rem',
              textAlign: 'center',
              backgroundImage: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.purple} 100%)`,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            <div className="max-w-3xl mx-auto">
              <h3 
                style={{
                  fontSize: window.innerWidth >= 768 ? '3rem' : '2.25rem',
                  fontWeight: 'bold',
                  color: colors.white,
                  marginBottom: '1.5rem'
                }}
              >
                Ready to Stop Guessing?<br />Start Knowing.
              </h3>
              <p 
                style={{
                  fontSize: '1.25rem',
                  color: colors.white,
                  marginBottom: '2rem'
                }}
              >
                Join business owners transforming their profitability with data-driven insights.
              </p>
              <a 
                href="/register"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: colors.white,
                  color: colors.navy,
                  padding: '1.25rem 2.5rem',
                  borderRadius: '0.5rem',
                  fontWeight: 'bold',
                  fontSize: '1.25rem',
                  transition: 'all 0.2s',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.purple;
                  e.target.style.color = colors.white;
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 35px 60px -15px rgba(0, 0, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = colors.white;
                  e.target.style.color = colors.navy;
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
                }}
              >
                Start Your Free 14-Day Trial
                <ArrowRight className="ml-3" size={24} />
              </a>
              <p style={{ marginTop: '1.5rem', fontSize: '0.875rem', color: colors.white }}>
                14 days free • Cancel anytime
              </p>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default PricingPage;